<template>
  <div class="feedback-page">
    <NavigationBar />

    <main class="feedback-card">
      <template v-if="submitted">
        <h1 class="title">{{ c.thanksTitle }}</h1>
        <p class="body">{{ c.thanksBody }}</p>
      </template>

      <template v-else>
        <h1 class="title">{{ c.pageTitle }}</h1>
        <p class="body subtitle">{{ c.pageSubtitle }}</p>

        <p v-if="!hasUid" class="notice">{{ c.missingUid }}</p>

        <form v-else novalidate @submit.prevent="submit">
          <fieldset
            v-for="q in c.questions"
            :id="'q-' + q.id"
            :key="q.id"
            class="question"
          >
            <legend class="question-label">
              {{ q.label }}
              <span v-if="q.required" class="required-star">*</span>
            </legend>
            <p v-if="q.help" class="question-help">{{ q.help }}</p>

            <!-- Single choice -->
            <template v-if="q.type === 'single'">
              <label
                v-for="opt in q.options"
                :key="opt.value"
                class="choice"
                :class="{ selected: answers[q.id] === opt.value }"
              >
                <input
                  v-model="answers[q.id]"
                  type="radio"
                  :name="q.id"
                  :value="opt.value"
                  @change="clearError(q.id)"
                />
                <span>{{ opt.label }}</span>
              </label>
            </template>

            <!-- Multiple choice (max 2, exclusive "none", free-text "other") -->
            <template v-else-if="q.type === 'multi'">
              <label
                v-for="opt in q.options"
                :key="opt.value"
                class="choice"
                :class="{
                  selected: isSelected(q, opt.value),
                  disabled: isDisabled(q, opt.value),
                }"
              >
                <input
                  type="checkbox"
                  :checked="isSelected(q, opt.value)"
                  :disabled="isDisabled(q, opt.value)"
                  @change="toggle(q, opt.value)"
                />
                <span>{{ opt.label }}</span>
              </label>

              <template v-if="q.allowOther">
                <label
                  class="choice"
                  :class="{
                    selected: isSelected(q, 'other'),
                    disabled: isDisabled(q, 'other'),
                  }"
                >
                  <input
                    type="checkbox"
                    :checked="isSelected(q, 'other')"
                    :disabled="isDisabled(q, 'other')"
                    @change="toggle(q, 'other')"
                  />
                  <span>{{ c.otherPlaceholder }}</span>
                </label>
                <input
                  v-if="isSelected(q, 'other')"
                  v-model.trim="answers[q.otherId]"
                  class="text-input other-input"
                  type="text"
                  maxlength="500"
                  :placeholder="c.otherPlaceholder"
                  :aria-label="c.otherPlaceholder"
                />
              </template>
            </template>

            <!-- Free text -->
            <textarea
              v-else-if="q.type === 'text'"
              v-model="answers[q.id]"
              class="text-input"
              rows="4"
              :maxlength="MAX_TEXT"
              :placeholder="c.textPlaceholder"
            ></textarea>

            <p v-if="errors[q.id]" class="error" role="alert">
              {{ errors[q.id] }}
            </p>
          </fieldset>

          <p class="required-note">{{ c.requiredNote }}</p>
          <p v-if="submitError" class="error" role="alert">{{ submitError }}</p>

          <button class="btn" type="submit" :disabled="submitting">
            {{ submitting ? c.submitting : c.submit }}
          </button>
        </form>
      </template>
    </main>

    <FooterComponent />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute } from "vue-router";
import NavigationBar from "@/components/NavigationBar.vue";
import FooterComponent from "@/components/Components/FooterComponent.vue";
import { apiBaseUrl } from "@/config/api";
import {
  appFeedbackContent,
  SURVEY_KEY,
  REQUIRED_QUESTIONS,
} from "@/i18n/appFeedback";

// Mirrors MAX_FEEDBACK_TEXT_LENGTH in the backend's survey.constants.ts.
const MAX_TEXT = 2000;

const route = useRoute();

const lang = computed(() => (route.query.lang === "en" ? "en" : "bg"));
const c = computed(() => appFeedbackContent[lang.value]);

const uid = computed(() =>
  typeof route.query.uid === "string" && route.query.uid ? route.query.uid : null
);
const hasUid = computed(() => !!uid.value);

// The question set is identical across languages (same ids, same codes) — only
// the labels differ — so the answer shape is built from either one.
const answers = reactive({});
for (const q of appFeedbackContent.bg.questions) {
  if (q.type === "multi") {
    answers[q.id] = [];
    if (q.otherId) answers[q.otherId] = "";
  } else {
    answers[q.id] = q.type === "text" ? "" : null;
  }
}

const errors = reactive({});
const submitting = ref(false);
const submitted = ref(false);
const submitError = ref("");

function clearError(id) {
  delete errors[id];
}

// --- Multi-select helpers (max N, exclusive "none", free-text "other") ---

function isSelected(q, value) {
  return answers[q.id].includes(value);
}

function isDisabled(q, value) {
  if (isSelected(q, value)) return false;
  // "None of the above" is exclusive.
  if (answers[q.id].includes("none")) return value !== "none";
  return answers[q.id].length >= q.maxSelect;
}

function toggle(q, value) {
  const arr = answers[q.id];
  const idx = arr.indexOf(value);
  if (idx >= 0) {
    arr.splice(idx, 1);
    if (value === "other" && q.otherId) answers[q.otherId] = "";
    clearError(q.id);
    return;
  }
  if (value === "none") {
    answers[q.id] = ["none"];
    if (q.otherId) answers[q.otherId] = "";
    clearError(q.id);
    return;
  }
  const withoutNone = arr.filter((v) => v !== "none");
  if (withoutNone.length >= q.maxSelect) return;
  answers[q.id] = [...withoutNone, value];
  clearError(q.id);
}

// --- Validation & submit ---

function validate() {
  Object.keys(errors).forEach((k) => delete errors[k]);

  for (const q of c.value.questions) {
    const v = answers[q.id];
    if (REQUIRED_QUESTIONS.includes(q.id)) {
      const empty = Array.isArray(v)
        ? v.length === 0
        : v === null || v === undefined || v === "";
      if (empty) errors[q.id] = c.value.errorRequired;
    }
    // "Other" always needs its free text — the backend rejects it otherwise.
    if (
      q.type === "multi" &&
      q.otherId &&
      v.includes("other") &&
      !answers[q.otherId].trim()
    ) {
      errors[q.id] = c.value.errorOther;
    }
  }
  return Object.keys(errors).length === 0;
}

function scrollToFirstError() {
  const firstId = c.value.questions.map((q) => q.id).find((id) => errors[id]);
  if (!firstId) return;
  const el = document.getElementById("q-" + firstId);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
}

// Only answered questions are sent — the DTO treats every answer as optional
// and required-ness is checked per survey on the backend.
function buildAnswers() {
  const out = {};
  for (const q of c.value.questions) {
    const v = answers[q.id];
    if (q.type === "multi") {
      if (v.length) out[q.id] = [...v];
      if (q.otherId && v.includes("other") && answers[q.otherId].trim()) {
        out[q.otherId] = answers[q.otherId].trim();
      }
    } else if (q.type === "text") {
      if (v.trim()) out[q.id] = v.trim();
    } else if (v !== null && v !== undefined) {
      out[q.id] = v;
    }
  }
  return out;
}

async function submit() {
  if (!hasUid.value) return;
  if (!validate()) {
    scrollToFirstError();
    return;
  }

  submitting.value = true;
  submitError.value = "";
  try {
    const payload = {
      userId: uid.value,
      locale: lang.value,
      answers: buildAnswers(),
    };

    const res = await fetch(
      `${apiBaseUrl}/api/surveys/${encodeURIComponent(SURVEY_KEY)}/responses`,
      {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      }
    );
    if (!res.ok) throw new Error(`Request failed with status ${res.status}`);

    submitted.value = true;
    window.scrollTo({ top: 0, behavior: "smooth" });
  } catch (err) {
    console.error("App feedback submit failed:", err);
    submitError.value = c.value.errorSubmit;
  } finally {
    submitting.value = false;
  }
}

onMounted(() => {
  document.title = c.value.metaTitle;
});
</script>

<style scoped lang="scss">
@import "~@/assets/scss/variables.scss";

.feedback-page {
  font-family: $font-regular;
  background: #f7f7f9;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.feedback-card {
  flex: 1;
  max-width: 680px;
  width: 100%;
  margin: 40px auto;
  padding: 32px 24px 40px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
}

.title {
  font-size: 26px;
  font-weight: 700;
  margin-bottom: 8px;
}

.body {
  font-size: 16px;
  line-height: 1.55;
}

.subtitle {
  color: #767676;
  margin-bottom: 24px;
}

.notice {
  background: #fbe9ec;
  border-radius: 10px;
  padding: 16px;
  font-size: 15px;
  line-height: 1.5;
}

.question {
  border: none;
  padding: 0;
  margin: 0 0 28px;
}

.question-label {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  padding: 0;
}

.scale {
  display: flex;
  gap: 8px;
}

.scale-option {
  flex: 1;
  padding: 12px 0;
  border: 1px solid #d8d8e0;
  border-radius: 10px;
  background: #ffffff;
  font-size: 16px;
  cursor: pointer;

  &.selected {
    background: #6b6b9c;
    border-color: #6b6b9c;
    color: #ffffff;
  }
}

.scale-legend {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #767676;
  margin-top: 6px;
}

.choice {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid #d8d8e0;
  border-radius: 10px;
  margin-bottom: 8px;
  font-size: 15px;
  cursor: pointer;

  &.selected {
    border-color: #6b6b9c;
    background: #f4f4f8;
  }
}

.text-input {
  width: 100%;
  border: 1px solid #d8d8e0;
  border-radius: 10px;
  padding: 12px 14px;
  font-family: inherit;
  font-size: 15px;
  resize: vertical;
}

.required-note {
  font-size: 13px;
  color: #767676;
  margin-bottom: 16px;
}

.error {
  color: #c0392b;
  font-size: 14px;
  margin-top: 8px;
}

.btn {
  background: #6b6b9c;
  color: #ffffff;
  border: none;
  border-radius: 50px;
  padding: 14px 32px;
  font-size: 16px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.question-help {
  font-size: 14px;
  color: #767676;
  margin: -6px 0 12px;
}

.required-star {
  color: #c0392b;
}

.choice {
  &.disabled {
    opacity: 0.45;
    cursor: default;
  }
}

.other-input {
  margin-top: 8px;
}
</style>
